import React, { useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import Grid from '@material-ui/core/Grid'
import { useIntl } from 'react-intl'
import messages from '@utils/messages'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Typography, Button, CircularProgress } from '@material-ui/core'
import DialogActions from '@material-ui/core/DialogActions'
import { useConfirmAffiliationMutation } from '@src/mutations/ConfirmAffiliationMutation.generated'
import { CURRENT_USER } from '@src/queries/CurrentUserQuery'
import {
  SnackbarMessages,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import clsx from 'clsx'
import { useCurrentUser } from '@utils/useCurrentUser'
import { useSoldToAccountsQuery } from '@src/queries/SoldToAccountsQuery.generated'
import { removeTypename } from '@utils/removeTypeName'

const useAccountStyles = makeStyles((theme: Theme) => ({
  root: {
    maxHeight: '550px',
    width: '620px',
    maxWidth: '620px',
    height: '99%',
    [theme.breakpoints.down('xs')]: {
      width: '320px',
      maxHeight: '568px',
      flexGrow: 0,
      padding: '16px 16px 18.5px 16px',
    },
  },
  mainNavLinkContainer: {
    display: 'flex',
    border: 'solid 2px #979197',
    borderRadius: '8px',
    margin: '0 0 16px 0',
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    padding: '14px',
    [theme.breakpoints.down('xs')]: {
      padding: '16px',
    },
  },
  selectedSoldTo: {
    borderColor: theme.palette.primary.main,
    background: theme.palette.primary.light,
  },
  iconAndLinkContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.typography.pxToRem(24),
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  addressText: {
    fontSize: theme.typography.pxToRem(16),
  },
  addressCompany: {
    fontWeight: 'bolder',
  },
  closeButton: {
    position: 'absolute',
    right: '32px',
    top: '32px',
    padding: 0,
    [theme.breakpoints.down('xs')]: {
      top: 16,
      right: 16,
    },
  },
  headerTextContainer: {
    padding: '32px 32px 16px 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '0 0 16px 0',
    },
    '& h2': {
      display: 'flex',
      alignItems: 'center',
      textTransform: 'uppercase',
      fontSize: '20px',
      fontWeight: '900',
      lineHeight: 'normal',
      letterSpacing: '1px',
    },
  },
  subText: {
    marginBottom: '24px',
    [theme.breakpoints.down('xs')]: {
      marginBottom: '16px',
    },
  },
  defaultLink: {
    whiteSpace: 'pre-wrap',
    textAlign: 'center',
    fontSize: theme.typography.pxToRem(12),
    cursor: 'pointer',
    fontWeight: 'lighter',
  },
  loaderImg: {
    width: '400px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconActive: {
    color: theme.palette.primary.main,
    marginBottom: theme.typography.pxToRem(8),
  },
  addressContainer: {
    width: '402px',
  },
  cancelBtn: {
    marginRight: '8px',
    [theme.breakpoints.down('xs')]: {
      display: 'none !important',
    },
  },
  dialogContent: {
    marginBottom: '8px',
    marginRight: '4px',
    padding: '0 32px',
    [theme.breakpoints.down('xs')]: {
      padding: '0 0 16.5px 0',
      margin: 0,
    },
  },
  mobileButton: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
      width: '100%',
      height: 47,
      marginLeft: '0 !important',
    },
  },
  dialogActions: {
    margin: '0 24px 24px 0',
    [theme.breakpoints.down('xs')]: {
      margin: 0,
      flexDirection: 'column',
    },
  },
  subHead: {
    paddingLeft: 32,
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  visbleMoble: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  iconContainer: {
    display: 'none',
    flexDirection: 'row',
    justifyContent: 'left',
    alignItems: 'center',
    marginBottom: theme.typography.pxToRem(16),
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  addressCompanyStyled: {
    fontWeight: 'bold',
    marginLeft: 8.5,
  },
}))

const SwitchAccountModal = ({ setSwitchAccountOpen }) => {
  const classes = useAccountStyles()
  const { setSnackbar } = useGlobalSnackbar()
  const { formatMessage } = useIntl()
  const { currentUser } = useCurrentUser()
  const [confirmAffiliationMutation, { loading: changeDefaultLoading }] =
    useConfirmAffiliationMutation({
      refetchQueries: [{ query: CURRENT_USER }],
    })

  const partnerInfo =
    currentUser?.__typename === 'LoggedInUser' && currentUser?.soldToPartners
      ? [...currentUser?.soldToPartners]
      : []

  const uniquePartners = [
    ...new Map(partnerInfo.map((item) => [item['soldTo'], item])).values(),
  ]
  const soldTos =
    uniquePartners.map((partnerInfoItem) => partnerInfoItem.soldTo) || []
  const { data, loading: addressDataLoading } = useSoldToAccountsQuery({
    variables: {
      soldTos: soldTos,
    },
    skip: soldTos.length < 1,
  })
  const [partnerList, setPartnerList] = useState<any>(uniquePartners)
  const defaultPartner = uniquePartners.find(
    (partnerItem) => partnerItem.isDefault
  )
  const [selectedSoldTo, setSelectedSoldTo] = useState(
    defaultPartner || ({} as any)
  )

  React.useEffect(() => {
    if (
      currentUser?.__typename === 'LoggedInUser' &&
      currentUser?.soldToPartners &&
      !addressDataLoading
    ) {
      const accounts = data?.getSoldToAccounts || []

      uniquePartners.forEach((info) => {
        const accountDetails = accounts.find((accountInfo) => {
          return accountInfo.accountNumber === info.soldTo
        })

        info['addressDetails'] = accountDetails || []
      })

      setPartnerList(uniquePartners)
    }
  }, [data, addressDataLoading])

  const onSubmit = async () => {
    try {
      const { data: updateDefaultPartner } = await confirmAffiliationMutation({
        variables: {
          data: {
            billTo: selectedSoldTo?.billTo,
            soldTo: selectedSoldTo?.soldTo,
            participantId: selectedSoldTo?.participantId,
            payer: selectedSoldTo?.payer,
            salesOrg: selectedSoldTo?.salesOrg,
            shipTo: selectedSoldTo?.shipTo,
            contact: selectedSoldTo?.contact,
            blueCustomerDetails: removeTypename(
              selectedSoldTo?.addressDetails?.blueCustomerDetails
            ),
          },
          isSwitchAccount: true,
        },
      })

      if (updateDefaultPartner?.confirmAffiliation) {
        setTimeout(() => {
          window.location.reload()
        }, 0)
        setSwitchAccountOpen(false)
      }
    } catch (e) {
      setSnackbar(SnackbarMessages.GenericError)
    }
  }

  const partnerListItems = partnerList.map((partnerItem, i) => {
    const streetAddresses =
      partnerItem?.addressDetails?.streetAddresses.join(' ')
    const cityState = `${partnerItem?.addressDetails?.city || ''}  ${
      partnerItem?.addressDetails?.state || ''
    } ${partnerItem?.addressDetails?.postalCode || ''}`
    const activeSelected =
      selectedSoldTo &&
      selectedSoldTo.soldTo === partnerItem.soldTo &&
      selectedSoldTo.shipTo === partnerItem.shipTo &&
      selectedSoldTo.payer === partnerItem.payer &&
      selectedSoldTo.billTo === partnerItem.billTo
    return (
      <Grid
        item
        xs={12}
        className={clsx(
          classes.mainNavLinkContainer,
          activeSelected && classes.selectedSoldTo
        )}
        key={i}
        onClick={() => {
          setSelectedSoldTo(partnerItem)
        }}
      >
        <Grid item className={classes.iconAndLinkContainer}>
          <img
            src="/static/switch-account/org.svg"
            alt="Org Icon"
            className={clsx(classes.iconActive)}
          />
          <Typography variant="body2" className={classes.addressCompany}>
            {partnerItem.soldTo}
          </Typography>
        </Grid>
        <Grid item className={classes.addressContainer}>
          <Grid item className={classes.iconContainer}>
            <img src="/static/switch-account/org.svg" alt="Org Icon" />
            <Typography
              variant="body2"
              className={classes.addressCompanyStyled}
            >
              {partnerItem.soldTo}
            </Typography>
          </Grid>
          <Typography variant="body2" className={classes.addressCompany}>
            {partnerItem?.addressDetails?.organizationName
              ? partnerItem.addressDetails.organizationName
              : ''}
          </Typography>
          <Typography variant="body1" className={classes.addressText}>
            {streetAddresses ? streetAddresses : ''}
          </Typography>
          <Typography variant="body1" className={classes.addressText}>
            {cityState ? cityState : ''}
          </Typography>
          <Typography variant="body1" className={classes.addressText}>
            {partnerItem?.addressDetails?.country
              ? partnerItem.addressDetails.country
              : ''}
          </Typography>
        </Grid>
      </Grid>
    )
  })

  return (
    <Dialog classes={{ paperWidthSm: classes.root }} open>
      <IconButton
        onClick={() => {
          setSwitchAccountOpen(false)
        }}
        className={classes.closeButton}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogTitle className={classes.headerTextContainer}>
        {formatMessage(messages.SWITCH_ACCOUNTS)}
      </DialogTitle>
      <Grid container alignItems="flex-end" className={classes.subHead}>
        <Grid item xs={12}>
          <p className={classes.subText}>
            {formatMessage(messages.SWITCH_ACCOUNTS_SUB)}
          </p>
        </Grid>
      </Grid>
      <DialogContent className={classes.dialogContent}>
        <Grid container alignItems="flex-end" style={{ maxHeight: '333px' }}>
          {addressDataLoading ? (
            <CircularProgress color="inherit" size={40} thickness={10} />
          ) : (
            partnerListItems
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setSwitchAccountOpen(false)
          }}
          className={clsx(classes.mobileButton, classes.cancelBtn)}
        >
          {formatMessage(messages.CANCEL)}
        </Button>
        <Button
          onClick={() => {
            onSubmit()
          }}
          color="primary"
          disabled={changeDefaultLoading}
          variant="contained"
          className={classes.mobileButton}
        >
          {changeDefaultLoading && (
            <CircularProgress color="secondary" size={20} thickness={5} />
          )}
          {formatMessage(messages.CONTINUE)}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setSwitchAccountOpen(false)
          }}
          className={clsx(classes.mobileButton, classes.visbleMoble)}
        >
          {formatMessage(messages.CANCEL)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SwitchAccountModal
